import { useGridApiRef } from "@mui/x-data-grid-pro";
import { useLanguage } from "../../../../stores/LanguageStore";
import { useCallback, useEffect, useMemo, useState } from "react";
import numberColumn from "../../../../layouts/Table/columns/numberColumn";
import { nameColumn } from "../../../../layouts/Table/columns/nameColumn";
import { getEmailTypeProps } from "../sendersUtil";
import { sharingColumn } from "../../../../layouts/Table/columns/sharingColumn";
import dateColumn from "../../../../layouts/Table/columns/dateColumn";
import SelectionDeleter from "../../../../components/TableComponents/SelectionDeleter";
import {
  deleteEmail,
  deleteLinkedinAccount,
  editLinkedinAccount,
  getLinkedinAccounts,
} from "../../../../api/EmailAPI";
import { useListingLoader } from "../../../../components/hooks/ListingHooks";
import {
  ActionIcon,
  Anchor,
  Avatar,
  CloseButton,
  CopyButton,
  Divider,
  Flex,
  Group,
  Stack,
  Text,
  ThemeIcon,
  Tooltip,
} from "@mantine/core";
import MainTable from "../../../../layouts/Table/MainTable";
import {
  IconBrandLinkedin,
  IconCheck,
  IconChevronDown,
  IconCompass,
  IconExclamationCircleFilled,
  IconQuestionMark,
  IconX,
} from "@tabler/icons-react";
import { useNavigate, useParams } from "react-router-dom";
import SelectionSharer from "../../../../components/TableComponents/SelectionSharer";
import FoldersDropdown from "../../lists/ListingWithContent/contacts/FoldersDropdown";
import {
  getRowClasses,
  minimizedSize,
} from "../../lists/ListingWithContent/listing";
import NoItemsModal from "../../../../components/TableComponents/NoItemsModal";
import { useListStore } from "../../../../stores/ListStore";
import CreateLinkedinSender from "./CreateLinkedinSender";
import LinkedinPage from "./LinkedinPage";
import LinkedinAccountActions from "./LinkedinAccountActions";
import {
  useAccountsStore,
  useNotificationStore,
} from "../../../../stores/ConfigStore";
import { sortByActiveId } from "../../../../functions/utilFunctions";

const LinkedinSendersListing = () => {
  const apiRef = useGridApiRef();
  const lang = useLanguage((s) => s.language);
  const { poolId, accountId } = useParams();
  const account_id = useMemo(() => parseInt(accountId) || null, [accountId]);

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);

  const columns = useMemo(() => {
    if (account_id) {
      return [
        nameColumn({
          disableLink: true,
          handleClick: () => {},
          // editFunction:
          //   () =>
          //   ({ id, name }) =>
          //     editLinkedinAccount({ id, data: { name } }),
          width: 330,
          editorStyle: { right: 32 },
          apiRef,
          leftSection: function (row) {
            return (
              <Avatar
                color="initials"
                name={row.name}
                src={row.linkedin_data?.image_url}
                radius={"xl"}
                size={36}
                variant="filled"
              />
            );
          },
        }),
      ];
    } else
      return [
        numberColumn(),
        nameColumn({
          editFunction:
            () =>
            ({ id, name }) =>
              editLinkedinAccount({ id, data: { name } }),
          apiRef,
          width: 200,
          editorStyle: { right: 16 },
          handleClick: () => {},
          disableLink: true,
          leftSection: function (row) {
            return (
              <Avatar
                color="initials"
                name={row.name}
                src={row.linkedin_data?.image_url}
                radius={"xl"}
                size={36}
                variant="filled"
              />
            );
          },
        }),
        {
          field: "is_sales_navigator_account",
          headerName: lang.linkedin_accounts.columns.is_sales_navigator_account,
          width: 125,
          renderCell: ({ value }) => (
            <Tooltip label={value ? "Sales Navigator" : "LinkedIn"}>
              <ThemeIcon
                variant="light"
                color={value ? "tertiary.8" : "var(--mantine-color-blue-7)"}
              >
                {value ? (
                  <IconCompass size={23} />
                ) : (
                  <IconBrandLinkedin size={23} />
                )}
              </ThemeIcon>
            </Tooltip>
          ),
        },
        {
          field: "is_valid",
          headerName: lang.emails_account.columns.is_valid_column,
          width: 90,
          renderCell: ({ value, row }) => {
            if (row.checkpoint_required) {
              return (
                <Tooltip label={lang.emails_account.verification_required}>
                  <ActionIcon
                    onClick={(e) => {
                      e.stopPropagation();
                      const needVerifIgnores =
                        useNotificationStore.getState().needVerifIgnores;
                      const setNeedVerifIgnores =
                        useNotificationStore.getState().setNeedVerifIgnores;
                      const setAccountVerif =
                        useAccountsStore.getState().setAccountVerif;

                      setNeedVerifIgnores(
                        needVerifIgnores.filter((x) => x !== row.id)
                      );

                      setAccountVerif({
                        ...row,
                        onFinish: () => setLoading(true),
                      });
                    }}
                    variant="filled"
                    color="red"
                    radius={"xl"}
                    size={"lg"}
                    pl={1}
                  >
                    <IconExclamationCircleFilled />
                  </ActionIcon>
                </Tooltip>
              );
            }
            if (!row?.validity_tested) {
              return (
                <Tooltip label={lang.emails_account.not_tested}>
                  <ThemeIcon
                    variant="light"
                    color={"orange"}
                    radius={"xl"}
                    size={"lg"}
                    pl={1}
                  >
                    <IconQuestionMark />
                  </ThemeIcon>
                </Tooltip>
              );
            }
            if (value && row?.validity_tested) {
              return (
                <Tooltip label={lang.emails_account.columns.is_valid}>
                  <ThemeIcon
                    variant="light"
                    color={"green"}
                    radius={"xl"}
                    size={"lg"}
                    pl={1}
                  >
                    <IconCheck />
                  </ThemeIcon>
                </Tooltip>
              );
            }
            if (!value) {
              return (
                <Tooltip label={lang.emails_account.columns.not_valid}>
                  <ThemeIcon
                    variant="light"
                    color={"red"}
                    radius={"xl"}
                    size={"lg"}
                    pl={1}
                  >
                    <IconX />
                  </ThemeIcon>
                </Tooltip>
              );
            }
          },
        },
        {
          field: "username",
          headerName: "Email",
          width: 200,
        },
        {
          field: "session_cookie",
          headerName: lang.linkedin_accounts.columns.session_cookie,
          width: 120,
          renderCell: ({ value }) => (
            <CopyButton value={value}>
              {({ copied, copy }) => (
                <Tooltip
                  label={copied ? lang.global.copied : lang.global.copy}
                  color={copied ? "green" : "black"}
                >
                  <Anchor
                    c={"black"}
                    onClick={(e) => {
                      e.stopPropagation();
                      copy();
                    }}
                    truncate="end"
                  >
                    {value}
                  </Anchor>
                </Tooltip>
              )}
            </CopyButton>
          ),
        },
        {
          field: "session_cookie_sn",
          headerName: lang.linkedin_accounts.columns.session_cookie + " SN",
          width: 120,
          renderCell: ({ value }) => (
            <CopyButton value={value}>
              {({ copied, copy }) => (
                <Tooltip
                  label={copied ? lang.global.copied : lang.global.copy}
                  color={copied ? "green" : "black"}
                >
                  <Anchor
                    c={"black"}
                    onClick={(e) => {
                      e.stopPropagation();
                      copy();
                    }}
                    truncate="end"
                  >
                    {value}
                  </Anchor>
                </Tooltip>
              )}
            </CopyButton>
          ),
        },
        // poolsColumn({
        //   apiRef,
        //   editFunction:
        //     () =>
        //     ({ id, name }) =>
        //       editLinkedinAccount({ id, data: { name } }),
        //   type: "Linkedin",
        // }),
        dateColumn({
          field: "last_use",
          headerName: lang.emails_account.columns.last_use,
        }),
        dateColumn(),
        sharingColumn({
          apiRef,
          editFunction: () => editLinkedinAccount,
        }),
      ];
  }, [account_id, apiRef, lang]);

  const actionsCell = useCallback(
    ({ row, value }) => {
      return (
        <Group wrap="nowrap" gap={4}>
          <LinkedinAccountActions
            current={row}
            onFinish={() => setLoading(true)}
          />

          <IconChevronDown
            size={24}
            className="bump"
            opacity={account_id === row.id ? 1 : 0.4}
            style={{
              transform: account_id === row.id ? "rotate(-90deg)" : "none",
              transition: "all ease .3s",
            }}
            onClick={() => handleRowClick({ row })}
          />
        </Group>
      );
    },
    [account_id]
  );

  const toolbarActions = useCallback(
    ({ selectedIds }) => (
      <>
        <SelectionDeleter
          handleDelete={deleteLinkedinAccount}
          apiRef={apiRef}
          setTableLoading={setLoading}
          ids={selectedIds}
          rows={rows}
        />
        <SelectionSharer
          handleShare={editLinkedinAccount}
          apiRef={apiRef}
          setTableLoading={setLoading}
          ids={selectedIds}
          rows={rows}
        />
        {/* <ReconnectAll setTableLoading={setLoading} ids={selectedIds} /> */}
      </>
    ),
    [apiRef, rows]
  );

  const navigate = useNavigate();
  const handleRowClick = ({ row }) => {
    const setMinimized = useListStore.getState().setModelMinimized;

    if (poolId) {
      if (account_id === row.id) {
        navigate(`/senders/pool/${poolId}/linkedin/none`);
      } else {
        navigate(`/senders/pool/${poolId}/linkedin/${row.id}`);
        setMinimized(true);
      }
    } else {
      if (account_id === row.id) {
        navigate(`/senders/linkedin/none`);
      } else {
        navigate(`/senders/linkedin/${row.id}`);
        setMinimized(true);
      }
    }
  };

  const handleClose = () => {
    if (poolId) {
      navigate(`/senders/pool/${poolId}/linkedin/none`);
    } else {
      navigate(`/senders/linkedin/none`);
    }
  };

  useEffect(() => {
    if (poolId !== "none") setLoading(true);
  }, [poolId]);

  useListingLoader({
    loading,
    setLoading,
    listGetter: getLinkedinAccounts,
    responseField: "linkedin_accounts_list",
    setRows,
    folderId: poolId,
    isPool: true,
  });

  return (
    <Stack flex={1} px={48} pt={16} gap={0} h={"calc(100vh - 70px)"}>
      <Group gap={"xl"}>
        <Text size="xl" fw={600} mt={16} mb={8} mr={"auto"}>
          {lang.new_senders.linkedin_title}
        </Text>
        <CreateLinkedinSender />
      </Group>

      {poolId && (
        <Group w={382} mt={8}>
          <FoldersDropdown
            type="Linkedin"
            getClickLink={(id) => `/senders/pool/${id}/linkedin/none`}
            getDeletedLink={() => `/senders/linkedin/none`}
            isPool={true}
          />
        </Group>
      )}
      <Divider my={16} />
      <Flex flex={1}>
        <Group
          flex={1}
          wrap="nowrap"
          align="stretch"
          gap={0}
          style={{ overflowX: "hidden" }}
          pt={64}
          mt={-64}
        >
          <Flex
            flex={1}
            maw={account_id ? minimizedSize - 48 : null}
            pos={"relative"}
          >
            <NoItemsModal
              rows={rows}
              loading={loading}
              text={
                <Text size="xl" mb={32}>
                  {lang.new_senders.no_linkedin}
                </Text>
              }
              button={<CreateLinkedinSender />}
            />
            <MainTable
              apiRef={apiRef}
              columns={columns}
              rows={sortByActiveId(rows, account_id)}
              loading={loading}
              setLoading={setLoading}
              id={"linkedin-table"}
              toolbarActions={account_id ? null : toolbarActions}
              actionsCell={actionsCell}
              onRowClick={handleRowClick}
              hasFolder
              pageName={"integrations_linkedin"}
              pageOrderName={"integrations_linkedin_order"}
              whiteButtons={!account_id}
              actionsCellWidth={75}
              disableGotoPage={account_id}
              disableVisibleColumns={account_id}
              defaultPerPage={15}
              disablePerPage={account_id}
              disableCheckboxes={!!account_id}
              withSorter={!!account_id}
              getRowClassName={(params) =>
                getRowClasses({
                  params,
                  mainId: accountId,
                  integerId: account_id,
                })
              }
              tableSx={{
                "& .MuiDataGrid-columnHeaders": {
                  border: "none !important",
                  display: account_id ? "none" : "flex",
                },
                "& .MuiDataGrid-cell": {
                  border: "none",
                },
                "& .MuiDataGrid-main": {
                  background: "var(--mantine-color-primary-2)",
                  border: "none !important",
                  borderRadius: 3,
                },
                "& .table-search": {
                  width: account_id ? "200px !important" : "300px !important",
                },
                "& .MuiDataGrid-footerContainer": {
                  border: "none !important",
                },
                "& .table-row": {
                  cursor: "pointer",
                  borderRadius: 3,
                  marginBottom: 1,
                  background: "#fff",
                  border: "1px solid var(--mantine-color-primary-1)",
                },
                "& .MuiDataGrid-pinnedColumnHeaders": {
                  background: "transparent",
                  borderRight: "none !important",
                },
                "& .MuiDataGrid-pinnedColumns--right": {
                  background: "var(--mantine-color-primary-2) !important",
                  border: "none !important",
                },
                "& .MuiDataGrid-pinnedColumns--left": {
                  background: "var(--mantine-color-primary-2) !important",
                  border: "none !important",
                },
                "& .MuiDataGrid-pinnedColumns--right .table-row": {
                  borderTopLeftRadius: 0,
                  borderBottomLeftRadius: 0,
                  background: "#fff",
                  border: "1px solid   var(--mantine-color-primary-1)",
                  borderLeft: "none !important",
                },
                "& .MuiDataGrid-pinnedColumns--left .table-row": {
                  borderTopRightRadius: 0,
                  borderBottomRightRadius: 0,
                  background: "#fff",
                  border: "1px solid   var(--mantine-color-primary-1)",
                  borderRight: "none !important",
                },
                "& .MuiDataGrid-virtualScrollerRenderZone .table-row": {
                  borderRadius: account_id ? 3 : 0,
                  borderTopRightRadius: 0,
                  borderBottomRightRadius: 0,
                },
                "& .MuiDataGrid-selectedRowCount": {
                  visibility: account_id ? "hidden" : "visible",
                },
                "& .MuiPagination-root": {
                  left: account_id ? 0 : undefined,
                  transform: account_id ? "none" : undefined,
                },
              }}
            />
          </Flex>

          <Stack
            flex={1}
            maw={account_id ? "none" : 0}
            pl={32}
            style={{ transition: "all ease .3s", overflow: "hidden" }}
            h={"auto"}
            pos={"relative"}
          >
            <CloseButton
              style={{
                position: "absolute",
                left: 48,
                top: 8,
                zIndex: 4,
              }}
              size={40}
              onClick={handleClose}
            />
            {account_id && !loading && (
              <LinkedinPage
                handleClose={handleClose}
                onEdited={() => {
                  handleClose();
                  setLoading(true);
                }}
              />
            )}
          </Stack>
        </Group>
      </Flex>
    </Stack>
  );
};

export default LinkedinSendersListing;
